// The schedule board's three panels (spec 03 §4.6): the block drawer that
// explains one operation and, for a planner on a larger screen, moves it; the
// overload panel that lists the late work behind a marker and, for a reader
// served them, its ranked and priced moves with Apply (P3-I-3, D-402); and the
// waiting draft's panel with Publish and Discard. Each is a `Modal`, which a
// phone shows as a full-screen sheet.

import { useState, type ReactNode } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/button";
import { Modal } from "../../../../components/modal";
import { CardSkeleton, ErrorState } from "../../../../components/states";
import { StatusChip } from "../../../../components/status-chip";
import { Banner } from "../../../../components/toast";
import { UnauthorizedError } from "../../../../lib/problem";
import { RequestError, requestIdOf } from "../../../../lib/request";
import { problemExtra } from "../hooks/planning-queries";
import {
  discardSchedule,
  publishSchedule,
  useChangeoverExplanation,
  useScheduleDiff,
  type ScheduleHeader,
  type ScheduleLane,
  type ScheduleOperation,
  type ScheduleOverload,
} from "../hooks/schedule-queries";
import { formatExactQty } from "../lib/mrp-lib";
import { formatDate, useOffline, weekLabel } from "../lib/planning-lib";
import { keyText, laneName, slackDays, wallMinutesOf, zonedStamp } from "../lib/schedule-lib";
import { formatHours } from "../lib/calendar-lib";
import { OperationMarks } from "./schedule-lanes";
import { SalesLineText, ScheduleOrderText, UnplacedReasonText } from "./schedule-bits";
import { MoveForm, type MoveRequest } from "./schedule-drag";
import { MovesSection, type MoveApplied } from "./schedule-moves";
import { shortId } from "../../../../lib/short-id";

function Fact({
  label,
  children,
  testId,
}: {
  label: string;
  children: ReactNode;
  testId?: string;
}) {
  return (
    <div className="planning-fact" data-testid={testId}>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}

export { laneName } from "../lib/schedule-lib";

// ---- the block drawer ------------------------------------------------------------------

export function BlockDrawer({
  op,
  facilityId,
  operations,
  lanes,
  timezone,
  move = "none",
  onPreview,
  onClose,
}: {
  op: ScheduleOperation;
  facilityId: string;
  /** Every operation the board holds, for the lane's previous key and the batch. */
  operations: readonly ScheduleOperation[];
  lanes: readonly ScheduleLane[];
  timezone: string;
  /**
   * The move form for a holder of `planning.schedule.publish` on the
   * published schedule, the phone's line for one on a phone, nothing else.
   */
  move?: "form" | "phone" | "none";
  onPreview?: (req: MoveRequest) => void;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const lane = lanes.find((l) => l.work_center_id === op.work_center_id);
  const position = op.sequence_index;
  const previous =
    position !== null
      ? operations.find(
          (o) => o.work_center_id === op.work_center_id && o.sequence_index === position - 1,
        )
      : undefined;
  const batch =
    op.batch_key === null
      ? []
      : operations.filter((o) => o.batch_key === op.batch_key && o.op_ref !== op.op_ref);
  const wall = wallMinutesOf(op.planned_start, op.planned_end);
  const slack = slackDays(op.planned_end, op.due_date, timezone);
  const order = op.label.order_no ?? t("planning.schedule.block.plannedOrder");
  return (
    <Modal
      label={t("planning.schedule.drawer.label", { order })}
      heading={
        <span className="planning-drawer-title">
          <ScheduleOrderText op={op} testId="planning-schedule-drawer-order" />
          <span>· {op.step_name}</span>
        </span>
      }
      closeLabel={t("planning.common.close")}
      onClose={onClose}
      className="planning-drawer planning-schedule-drawer"
      testId="planning-schedule-drawer"
    >
      <div {...{ "data-op-ref": op.op_ref }}>
        <OperationMarks op={op} />
        <dl className="planning-facts">
          <Fact label={t("planning.schedule.drawer.product")}>
            {[op.product_sku, op.product_name].filter((v) => v !== null).join(" · ") ||
              shortId(op.product_id)}
          </Fact>
          <Fact label={t("planning.schedule.drawer.qty")}>
            {formatExactQty(op.qty, i18n.language)} {op.uom}
          </Fact>
          {op.label.sales_order !== null ? (
            <Fact label={t("planning.schedule.drawer.salesOrder")}>
              <SalesLineText label={op.label} />
            </Fact>
          ) : null}
          <Fact label={t("planning.schedule.drawer.step")}>
            {t("planning.schedule.drawer.stepText", { no: op.step_no, name: op.step_name })}
          </Fact>
          <Fact label={t("planning.schedule.drawer.workCenter")}>
            {laneName(lane, op.work_center_id)}
          </Fact>
          {op.placement === "placed" ? (
            <>
              <Fact
                label={t("planning.schedule.drawer.start")}
                testId="planning-schedule-drawer-start"
              >
                {zonedStamp(op.planned_start, i18n.language, timezone)}
              </Fact>
              <Fact label={t("planning.schedule.drawer.end")} testId="planning-schedule-drawer-end">
                {zonedStamp(op.planned_end, i18n.language, timezone)}
              </Fact>
              {op.sequence_index !== null ? (
                <Fact label={t("planning.schedule.drawer.sequence")}>{op.sequence_index}</Fact>
              ) : null}
            </>
          ) : (
            <Fact label={t("planning.schedule.drawer.notPlaced")}>
              <UnplacedReasonText reason={op.unplaced_reason} />
            </Fact>
          )}
          <Fact label={t("planning.schedule.drawer.run")} testId="planning-schedule-drawer-run">
            {wall !== undefined && op.placement === "placed"
              ? t("planning.schedule.drawer.runText", {
                  run: op.run_minutes,
                  setup: op.setup_minutes,
                  wall,
                })
              : t("planning.schedule.drawer.runOnly", { run: op.run_minutes })}
          </Fact>
          <Fact label={t("planning.schedule.drawer.due")} testId="planning-schedule-drawer-due">
            {op.due_date === null ? (
              t("planning.schedule.drawer.noDue")
            ) : (
              <>
                {formatDate(op.due_date, i18n.language)}
                {slack !== undefined ? (
                  <span
                    className={slack < 0 ? "planning-bad" : "planning-hint"}
                    data-testid="planning-schedule-drawer-slack"
                    data-days={slack}
                  >
                    {" "}
                    {slack < 0
                      ? t("planning.schedule.drawer.lateBy", { count: -slack })
                      : slack === 0
                        ? t("planning.schedule.drawer.dueSameDay")
                        : t("planning.schedule.drawer.spare", { count: slack })}
                  </span>
                ) : null}
              </>
            )}
          </Fact>
          <Fact label={t("planning.schedule.drawer.priority")}>{op.priority}</Fact>
          <Fact label={t("planning.schedule.drawer.batch")} testId="planning-schedule-drawer-batch">
            {op.batch_key === null
              ? t("planning.schedule.drawer.noBatch")
              : batch.length === 0
                ? t("planning.schedule.drawer.batchKeyOnly", { key: keyText(op.setup_key) })
                : t("planning.schedule.drawer.batchWith", {
                    key: keyText(op.setup_key),
                    orders: batch
                      .map((o) => o.label.order_no ?? t("planning.schedule.block.plannedOrder"))
                      .join(", "),
                  })}
          </Fact>
        </dl>
        <Changeover op={op} previous={previous} facilityId={facilityId} />
        {move === "form" && onPreview !== undefined ? (
          <MoveForm op={op} lanes={lanes} timezone={timezone} onPreview={onPreview} />
        ) : null}
        {move === "phone" ? (
          <p className="planning-hint" data-testid="planning-schedule-move-phone">
            {t("planning.schedule.move.phone")}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}

/**
 * How the changeover was priced: the charged minutes, then the kernel's own
 * explanation from the lane's previous key (row 54a), each facet that changed
 * with the row that priced it. The routing step's static minutes are not on
 * the board, so a basis that falls back to them says so in words.
 */
function Changeover({
  op,
  previous,
  facilityId,
}: {
  op: ScheduleOperation;
  previous: ScheduleOperation | undefined;
  facilityId: string;
}) {
  const { t } = useTranslation();
  const ask =
    previous !== undefined && op.setup_key !== null && previous.setup_key !== null
      ? { from: previous.setup_key, to: op.setup_key, staticMinutes: "" }
      : undefined;
  const explain = useChangeoverExplanation(
    facilityId,
    ask === undefined ? undefined : op.work_center_id,
    ask,
  );
  return (
    <section
      className="planning-drawer-section"
      data-testid="planning-schedule-drawer-changeover"
      data-minutes={op.setup_minutes}
    >
      <h3>{t("planning.schedule.drawer.changeover")}</h3>
      <p>
        {op.setup_minutes === 0
          ? t("planning.schedule.drawer.noChangeover")
          : t("planning.schedule.drawer.changeoverMinutes", { minutes: op.setup_minutes })}
      </p>
      {op.setup_key !== null ? (
        <p className="planning-hint">
          {t("planning.schedule.drawer.key", { key: keyText(op.setup_key) })}
        </p>
      ) : null}
      {ask === undefined ? (
        <p className="planning-hint" data-testid="planning-schedule-drawer-no-previous">
          {previous === undefined
            ? t("planning.schedule.drawer.previousUnknown")
            : t("planning.schedule.drawer.keyMissing")}
        </p>
      ) : explain.isPending ? (
        <CardSkeleton lines={2} label={t("planning.schedule.drawer.explaining")} />
      ) : explain.error !== null || explain.data === undefined ? (
        <p className="planning-bad">{t("planning.schedule.drawer.explainError")}</p>
      ) : (
        <div data-testid="planning-schedule-drawer-explain" data-basis={explain.data.basis}>
          <p className="planning-hint">
            {t(`planning.matrix.basis.${explain.data.basis}`, {
              from: keyText(previous?.setup_key ?? null),
            })}
          </p>
          <ul className="planning-line-list">
            {explain.data.facets.map((f) => (
              <li key={f.facet} data-testid="planning-schedule-drawer-facet" data-facet={f.facet}>
                {f.row === null
                  ? t("planning.matrix.test.facetNoRow", { facet: f.facet, from: f.from, to: f.to })
                  : t("planning.matrix.test.facetRow", {
                      facet: f.facet,
                      from: f.from,
                      to: f.to,
                      rowFrom: f.row.from_value,
                      rowTo: f.row.to_value,
                      minutes: f.row.minutes,
                    })}
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

// ---- the overload panel ----------------------------------------------------------------

export function OverloadPanel({
  overload,
  lanes,
  timezone,
  has,
  canReadMembers,
  onClose,
  onApplied,
  onRefresh,
  onSuperseded,
}: {
  overload: ScheduleOverload;
  lanes: readonly ScheduleLane[];
  timezone: string;
  has: (code: string) => boolean;
  canReadMembers: boolean;
  onClose: () => void;
  onApplied: (applied: MoveApplied) => void;
  onRefresh: () => Promise<void>;
  onSuperseded: () => void;
}) {
  const { t, i18n } = useTranslation();
  const lane = lanes.find((l) => l.work_center_id === overload.work_center_id);
  return (
    <Modal
      label={t("planning.schedule.overload.title", {
        wc: laneName(lane, overload.work_center_id),
        week: weekLabel(overload.week_start, i18n.language),
      })}
      heading={t("planning.schedule.overload.title", {
        wc: laneName(lane, overload.work_center_id),
        week: weekLabel(overload.week_start, i18n.language),
      })}
      closeLabel={t("planning.common.close")}
      onClose={onClose}
      className="planning-drawer"
      testId="planning-schedule-overload-panel"
    >
      <div data-overload-id={overload.id} data-hours={overload.overload_hours}>
        <p data-testid="planning-schedule-overload-body">
          {t("planning.schedule.overload.body", {
            hours: overload.overload_hours,
            date: formatDate(overload.peak_date, i18n.language),
            required: formatHours(overload.required_minutes, i18n.language),
            capacity: formatHours(overload.capacity_minutes, i18n.language),
          })}
        </p>
        <h3>{t("planning.schedule.overload.lateOps", { count: overload.late_op_count })}</h3>
        {overload.late_operations.length === 0 ? (
          <p className="planning-hint">{t("planning.schedule.overload.noLateOps")}</p>
        ) : (
          <ul className="planning-line-list">
            {overload.late_operations.map((o) => (
              <li key={o.op_ref} data-testid="planning-schedule-overload-op" data-op-ref={o.op_ref}>
                <span>{o.label.order_no ?? t("planning.schedule.block.plannedOrder")}</span>
                {o.due_date !== null ? (
                  <span className="planning-hint">
                    {" "}
                    {t("planning.schedule.overload.due", {
                      date: formatDate(o.due_date, i18n.language),
                    })}
                  </span>
                ) : null}
                {o.placement === "unplaced" ? (
                  <span className="planning-warn"> {t("planning.schedule.overload.unplaced")}</span>
                ) : null}
              </li>
            ))}
          </ul>
        )}
        {overload.late_op_count > overload.late_operations.length ? (
          <p className="planning-hint">
            {t("planning.schedule.overload.more", {
              count: overload.late_op_count - overload.late_operations.length,
            })}
          </p>
        ) : null}
        {/* The server serves the moves to a holder of the publish or the cost
            code and leaves the key out for every other reader (§7 (b)). */}
        {overload.proposed_moves !== undefined ? (
          <MovesSection
            overload={overload}
            lanes={lanes}
            timezone={timezone}
            has={has}
            canReadMembers={canReadMembers}
            onApplied={onApplied}
            onRefresh={onRefresh}
            onSuperseded={onSuperseded}
          />
        ) : null}
      </div>
    </Modal>
  );
}

// ---- the waiting draft ------------------------------------------------------------------

export interface DraftOutcome {
  readonly kind: "published" | "discarded" | "superseded" | "decided";
  readonly version?: number | undefined;
}

/**
 * The waiting draft against the published schedule (row 62): the operations
 * it moves, its new and cleared overloads and the tests that held it, with
 * Publish and Discard for a holder of `planning.schedule.publish`. A Publish
 * answered 409 `SCHEDULE_NOT_DRAFT` opens the draft that `newest_draft_id`
 * names, with a line saying the plan changed.
 */
export function DraftPanel({
  draft,
  facilityId,
  lanes,
  timezone,
  canDecide,
  onClose,
  onDecided,
  onRefresh,
}: {
  draft: Pick<ScheduleHeader, "id" | "version" | "auto_publish">;
  facilityId: string;
  lanes: readonly ScheduleLane[];
  timezone: string;
  canDecide: boolean;
  onClose: () => void;
  onDecided: (outcome: DraftOutcome) => void;
  onRefresh: () => Promise<void>;
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const offline = useOffline();
  const [current, setCurrent] = useState(draft);
  const [changed, setChanged] = useState(false);
  const [pending, setPending] = useState<"publish" | "discard" | undefined>(undefined);
  const [failure, setFailure] = useState<unknown>(undefined);
  const diff = useScheduleDiff(facilityId, current.id);
  const held = diff.data?.auto_publish?.held_by ?? current.auto_publish?.held_by ?? [];

  const decide = async (what: "publish" | "discard") => {
    setPending(what);
    setFailure(undefined);
    try {
      const header =
        what === "publish" ? await publishSchedule(current.id) : await discardSchedule(current.id);
      await onRefresh();
      onDecided({
        kind: what === "publish" ? "published" : "discarded",
        version: header.version,
      });
    } catch (error) {
      if (error instanceof UnauthorizedError) {
        void navigate({ to: "/login" });
        return;
      }
      const code = error instanceof RequestError ? error.code : undefined;
      if (code === "SCHEDULE_NOT_DRAFT") {
        const newest = problemExtra(error, "newest_draft_id");
        await onRefresh();
        if (typeof newest === "string" && newest !== current.id) {
          setCurrent({ id: newest, version: current.version, auto_publish: null });
          setChanged(true);
        } else {
          onDecided({ kind: "decided" });
        }
      } else if (code === "SCHEDULE_SUPERSEDED") {
        await onRefresh();
        onDecided({ kind: "superseded" });
      } else {
        setFailure(error);
      }
    } finally {
      setPending(undefined);
    }
  };

  const nameOf = (id: string | null) =>
    id === null
      ? "–"
      : laneName(
          lanes.find((l) => l.work_center_id === id),
          id,
        );

  return (
    <Modal
      label={t("planning.schedule.draft.title")}
      heading={t("planning.schedule.draft.title")}
      closeLabel={t("planning.common.close")}
      onClose={onClose}
      pending={pending !== undefined}
      className="planning-drawer"
      testId="planning-schedule-draft-panel"
    >
      <div data-schedule-id={current.id}>
        {changed ? (
          <Banner kind="warn" data-testid="planning-schedule-draft-changed">
            {t("planning.schedule.draft.notDraft")}
          </Banner>
        ) : null}
        {failure !== undefined ? (
          <Banner kind="bad" data-testid="planning-schedule-draft-failure">
            {t("planning.schedule.draft.failed", { requestId: requestIdOf(failure) ?? "-" })}
          </Banner>
        ) : null}
        {held.length > 0 ? (
          <div data-testid="planning-schedule-held">
            <p>{t("planning.schedule.draft.heldBy")}</p>
            <ul className="planning-line-list">
              {held.map((h) => (
                <li key={h} data-testid="planning-schedule-held-by" data-held-by={h}>
                  {t(`planning.schedule.held.${h}`)}
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        {diff.isPending ? (
          <CardSkeleton lines={4} label={t("planning.schedule.draft.loading")} />
        ) : diff.error !== null || diff.data === undefined ? (
          <ErrorState
            title={t("planning.schedule.draft.loadError")}
            retryLabel={t("planning.common.retry")}
            onRetry={() => void diff.refetch()}
            requestId={requestIdOf(diff.error)}
            requestIdLabel={t("planning.common.requestId")}
          />
        ) : (
          <>
            {diff.data.base_schedule_id === null ? (
              <p className="planning-note" data-testid="planning-schedule-draft-first">
                {t("planning.schedule.draft.first")}
              </p>
            ) : null}
            <h3>{t("planning.schedule.draft.moved", { count: diff.data.moved_ops_count })}</h3>
            {diff.data.moved_ops.length === 0 ? (
              <p className="planning-hint">{t("planning.schedule.draft.movedNone")}</p>
            ) : (
              <ul className="planning-diff-list">
                {diff.data.moved_ops.map((m) => (
                  <li
                    key={m.op_ref}
                    data-testid="planning-schedule-draft-moved"
                    data-op-ref={m.op_ref}
                    data-order-no={m.label.order_no ?? ""}
                    data-new-start={m.new_start ?? ""}
                  >
                    <span className="planning-diff-order">
                      {m.label.order_no ?? t("planning.schedule.block.plannedOrder")}
                    </span>
                    <span>
                      {t("planning.schedule.draft.movedText", {
                        from:
                          m.old_start === null
                            ? "–"
                            : zonedStamp(m.old_start, i18n.language, timezone),
                        to:
                          m.new_start === null
                            ? "–"
                            : zonedStamp(m.new_start, i18n.language, timezone),
                      })}
                    </span>
                    {m.old_work_center_id !== m.new_work_center_id ? (
                      <span className="planning-hint">
                        {t("planning.schedule.draft.laneChanged", {
                          from: nameOf(m.old_work_center_id),
                          to: nameOf(m.new_work_center_id),
                        })}
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
            {diff.data.moved_ops_count > diff.data.moved_ops.length ? (
              <p className="planning-hint">
                {t("planning.schedule.draft.more", {
                  count: diff.data.moved_ops_count - diff.data.moved_ops.length,
                })}
              </p>
            ) : null}
            <h3>
              {t("planning.schedule.draft.newOverloads", { count: diff.data.new_overloads_count })}
            </h3>
            <OverloadRefs
              refs={diff.data.new_overloads}
              nameOf={nameOf}
              testId="planning-schedule-draft-new-overload"
              bad
            />
            <h3>
              {t("planning.schedule.draft.clearedOverloads", {
                count: diff.data.cleared_overloads_count,
              })}
            </h3>
            <OverloadRefs
              refs={diff.data.cleared_overloads}
              nameOf={nameOf}
              testId="planning-schedule-draft-cleared-overload"
            />
          </>
        )}
        {canDecide ? (
          <div className="planning-actions planning-draft-actions">
            <Button
              variant="danger"
              busy={pending === "discard"}
              busyLabel={t("planning.schedule.draft.discarding")}
              disabled={offline || pending !== undefined}
              data-testid="planning-schedule-discard"
              onClick={() => void decide("discard")}
            >
              {t("planning.schedule.draft.discard")}
            </Button>
            <Button
              variant="primary"
              busy={pending === "publish"}
              busyLabel={t("planning.schedule.draft.publishing")}
              disabled={offline || pending !== undefined}
              data-testid="planning-schedule-publish"
              onClick={() => void decide("publish")}
            >
              {t("planning.schedule.draft.publish")}
            </Button>
          </div>
        ) : (
          <p className="planning-hint" data-testid="planning-schedule-draft-read-only">
            {t("planning.schedule.draft.readOnly")}
          </p>
        )}
        {canDecide && offline ? (
          <p className="planning-note">{t("planning.common.offlineWrites")}</p>
        ) : null}
      </div>
    </Modal>
  );
}

function OverloadRefs({
  refs,
  nameOf,
  testId,
  bad = false,
}: {
  refs: readonly { work_center_id: string; week_start: string; overload_hours: string }[];
  nameOf: (id: string) => string;
  testId: string;
  bad?: boolean;
}) {
  const { t, i18n } = useTranslation();
  if (refs.length === 0)
    return <p className="planning-hint">{t("planning.schedule.draft.none")}</p>;
  return (
    <ul className="planning-line-list">
      {refs.map((r) => (
        <li
          key={`${r.work_center_id}|${r.week_start}`}
          data-testid={testId}
          data-week={r.week_start}
        >
          <StatusChip variant={bad ? "bad" : "ok"} dot={false}>
            {t("planning.schedule.overload.marker", { hours: r.overload_hours })}
          </StatusChip>{" "}
          {nameOf(r.work_center_id)} · {weekLabel(r.week_start, i18n.language)}
        </li>
      ))}
    </ul>
  );
}
